import { useMemo } from 'react';
import { PalettePageMatch, usePalettePageMatches } from './usePalettePageMatches';
import { PaletteResult, PaletteResultSection, usePaletteSearch } from './usePaletteSearch';

/** One row the keys can select, in screen order. */
export type PaletteEntry =
  | { kind: 'result'; result: PaletteResult }
  | { kind: 'page'; match: PalettePageMatch }
  | { kind: 'seeAll' };

export interface PaletteEntries {
  sections: PaletteResultSection[];
  pages: PalettePageMatch[];
  isSearching: boolean;
  /** The text is not empty: the last row opens the Search page with it. */
  hasText: boolean;
  /** Every selectable row: the artists, tracks and playlists, the pages, then "See all" (design 5d). */
  entries: PaletteEntry[];
}

/** The rows of the palette for the search text. */
export function usePaletteEntries(query: string): PaletteEntries {
  const { sections, isSearching } = usePaletteSearch(query);
  const pages = usePalettePageMatches(query);
  const hasText = query.trim().length > 0;

  const entries = useMemo(() => {
    const list: PaletteEntry[] = [];
    for (const section of sections) {
      for (const result of section.results) {
        list.push({ kind: 'result', result });
      }
    }
    for (const match of pages) {
      list.push({ kind: 'page', match });
    }
    if (hasText) {
      list.push({ kind: 'seeAll' });
    }
    return list;
  }, [hasText, pages, sections]);

  return { sections, pages, isSearching, hasText, entries };
}
